import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';
import { AIFireService } from './fire.service';
import { Message, Conversation } from '../models/chat.model';
import { ref, set, get, child, remove } from 'firebase/database';
import { database } from './fire-init'; 

@Injectable({
  providedIn: 'root'
})
export class ChatFireService {
  private conversationsSubject = new BehaviorSubject<Conversation[]>([]);
  private currentConversationSubject = new BehaviorSubject<Conversation | null>(null);
  private isLoadingSubject = new BehaviorSubject<boolean>(false);

  conversations$ = this.conversationsSubject.asObservable();
  currentConversation$ = this.currentConversationSubject.asObservable();
  isLoading$ = this.isLoadingSubject.asObservable();

  constructor(private aiService: AIFireService) {
    this.loadConversations();
  }

  private async loadConversations(): Promise<void> {
    try {
      const dbRef = ref(database);
      const snapshot = await get(child(dbRef, 'conversations'));
      const conversations: Conversation[] = [];
      if (snapshot.exists()) {
        const data = snapshot.val();
        const sortedKeys = Object.keys(data)
          .map(key => ({ key, updatedAt: new Date(data[key].updatedAt) }))
          .sort((a, b) => b.updatedAt.getTime() - a.updatedAt.getTime())
          .slice(0, 10);
        for (const { key } of sortedKeys) {
          const conversation = data[key] as Conversation;
          conversation.createdAt = new Date(conversation.createdAt);
          conversation.updatedAt = new Date(conversation.updatedAt);
          conversation.messages = conversation.messages.map(msg => ({
            ...msg,
            timestamp: new Date(msg.timestamp)
          }));
          conversations.push(conversation);
        }
      }
      this.conversationsSubject.next(conversations);
    } catch (error) {
      console.error('Error loading conversations from Realtime Database:', error);
    }
  }

  private async saveConversationToDatabase(conversation: Conversation): Promise<void> {
    try {
      await set(ref(database, `conversations/${conversation.id}`), conversation);
      console.log(`Conversation ${conversation.id} written to Firebase successfully.`);
    } catch (error) {
      console.error('Error saving conversation to Realtime Database:', error);
    }
  }

  private updateConversationInMemory(updatedConversation: Conversation): void {
    const existing = this.conversationsSubject.value.filter(conv => conv.id !== updatedConversation.id);
    const updatedList = [updatedConversation, ...existing].slice(0, 10);
    this.conversationsSubject.next(updatedList);
    this.currentConversationSubject.next(updatedConversation);
  }

  async updateConversation(updatedConversation: Conversation): Promise<void> {
    updatedConversation.updatedAt = new Date();
    this.updateConversationInMemory(updatedConversation);
    await this.saveConversationToDatabase(updatedConversation);
  }

  async createNewConversation(): Promise<Conversation> {
    const newConversation: Conversation = {
      id: uuidv4(),
      title: 'New Conversation',
      messages: [],
      createdAt: new Date(),
      updatedAt: new Date()
    };
    const conversations = [newConversation, ...this.conversationsSubject.value].slice(0, 10);
    this.conversationsSubject.next(conversations);
    this.currentConversationSubject.next(newConversation);
    await this.saveConversationToDatabase(newConversation);
    return newConversation;
  }

  selectConversation(conversationId: string): void {
    const conversation = this.conversationsSubject.value.find(c => c.id === conversationId) || null;
    this.currentConversationSubject.next(conversation);
  }

  async sendMessage(content: string): Promise<void> {
    if (!content.trim()) return;

    let conversation = this.currentConversationSubject.value;
    if (!conversation) {
      conversation = await this.createNewConversation();
    }

    const userMessage: Message = {
      id: uuidv4(),
      role: 'user',
      content,
      timestamp: new Date()
    };

    conversation.messages.push(userMessage);
    if (conversation.messages.length === 1) {
      conversation.title = content.length > 30 ? `${content.substring(0, 30)}...` : content;
    }
    await this.updateConversation(conversation);

    this.isLoadingSubject.next(true);
    try {
      const limitedHistory = conversation.messages.slice(-5);
      const aiResponse = await this.aiService.generateText(content, limitedHistory);

      const assistantMessage: Message = {
        id: uuidv4(),
        role: 'assistant',
        content: aiResponse,
        timestamp: new Date()
      };
      conversation.messages.push(assistantMessage);
      await this.updateConversation(conversation);
    } catch (error) {
      console.error('Error getting AI response', error);
      const errorMessage: Message = {
        id: uuidv4(),
        role: 'assistant',
        content: 'Sorry, I encountered an error while processing your request.',
        timestamp: new Date()
      };
      conversation.messages.push(errorMessage);
      await this.updateConversation(conversation);
    } finally {
      this.isLoadingSubject.next(false);
    }
  }

  async deleteConversation(conversationId: string): Promise<void> {
    const conversations = this.conversationsSubject.value.filter(c => c.id !== conversationId);
    this.conversationsSubject.next(conversations);
    if (this.currentConversationSubject.value?.id === conversationId) {
      this.currentConversationSubject.next(null);
    }
    try {
      await remove(ref(database, `conversations/${conversationId}`));
    } catch (error) {
      console.error('Error deleting conversation from Realtime Database:', error);
    }
  }

  async clearAllConversations(): Promise<void> {
    this.conversationsSubject.next([]);
    this.currentConversationSubject.next(null);
  }
}
